import { Link } from 'react-router-dom'
import { useI18n } from '@/lib/i18n'
import { useSectionHeading } from '@/lib/primarySection'
import './ProjectSection.css'

/** A logo: one file, one file per theme, or a light-on-dark file inverted for light mode. */
type Logo = string | { light: string; dark: string } | { src: string; invertOnLight?: boolean }

type Project = {
  id: string
  logos: Logo[]
  status?: 'live' | 'live-ongoing' | 'in-development'
  visible?: boolean
  title: string
  summary: string
  partners?: string[]
  stack?: string[]
  apps?: string[]
  demo?: string
}

type Content = {
  eyebrow: string
  title: string
  lede: string
  statusLabels: Record<string, string>
  partnersLabel: string
  demoCta: string
  demoNote: string
  appsLabel: string
  stackLabel: string
  items: Project[]
}

export default function ProjectsSection() {
  const { section, to } = useI18n()
  const c = section('projects') as unknown as Content
  const items = (Array.isArray(c.items) ? c.items : []).filter((p) => p.visible !== false)
  if (items.length === 0) return null

  const { Tag: Title, className: titleClass } = useSectionHeading('projects', 'h2')

  return (
    <section className="section work" aria-labelledby="work-title">
      <div className="section-head">
        <div>
          <span className="eyebrow">{c.eyebrow}</span>
          <Title id="work-title" className={titleClass} style={{ marginTop: 12 }}>
            {c.title}
          </Title>
        </div>
        <p className="lede">{c.lede}</p>
      </div>

      <div className="work__list">
        {items.map((p, i) => (
          <article className="work__item" key={p.id} aria-labelledby={`work-${p.id}`}>
            <div className="work__media" data-project={p.id}>
              {p.logos?.map((logo, n) => {
                const alt = n === 0 ? p.title : ''
                const loading = i === 0 ? 'eager' : 'lazy'
                if (typeof logo === 'string') return <img key={logo} src={logo} alt={alt} loading={loading} />
                if ('light' in logo)
                  return (
                    <span key={logo.dark} className="work__logo-pair">
                      <img className="work__logo--light" src={logo.light} alt={alt} loading={loading} />
                      <img className="work__logo--dark" src={logo.dark} alt="" loading={loading} aria-hidden />
                    </span>
                  )
                return <img key={logo.src} src={logo.src} alt={alt} loading={loading} data-invert-on-light={logo.invertOnLight || undefined} />
              })}
            </div>

            <div className="work__body">
              <div className="work__meta">
                <span className="mono muted">{String(i + 1).padStart(2, '0')}</span>
                {p.status && c.statusLabels?.[p.status] && (
                  <span className={`tag ${p.status.startsWith('live') ? 'tag--ok' : 'tag--accent'}`}>
                    <span className="dot" />
                    {c.statusLabels[p.status]}
                  </span>
                )}
                {p.partners?.map((s) => (
                  <span className="tag" key={s}>
                    {s}
                  </span>
                ))}
              </div>

              <h3 id={`work-${p.id}`}>{p.title}</h3>
              <p className="work__summary">{p.summary}</p>

              <dl className="work__facts">
                {p.apps && p.apps.length > 0 && (
                  <div>
                    <dt>{c.appsLabel}</dt>
                    <dd>
                      <ul>
                        {p.apps.map((a) => (
                          <li key={a}>{a}</li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                )}
                {p.stack && p.stack.length > 0 && (
                  <div>
                    <dt>{c.stackLabel}</dt>
                    <dd className="mono">{p.stack.join(' · ')}</dd>
                  </div>
                )}
              </dl>

              {p.demo && (
                <div className="work__cta">
                  <Link to={to(p.demo)} className="btn btn--primary">
                    {c.demoCta}
                    <span className="arrow" aria-hidden>
                      →
                    </span>
                  </Link>
                  <span className="mono muted">{c.demoNote}</span>
                </div>
              )}
            </div>
          </article>
        ))}
      </div>
    </section>
  )
}
